import { useEffect, useState, useCallback } from "react";

/**
 * Minimal shape of the `beforeinstallprompt` event. Not yet in the
 * standard TS DOM lib, so it's typed locally.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

/**
 * "Install app" affordance. Hidden until the browser fires
 * `beforeinstallprompt` (Chrome/Edge/Android), hidden again once the app is
 * installed (or already running standalone). Safari/iOS never fires this
 * event — those users install via the native share-sheet "Add to Home
 * Screen" flow instead, so the button simply never appears there.
 */
export function InstallButton({ className }: { className?: string }) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari exposes this non-standard flag when launched from the home screen
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    setInstalled(isStandalone);

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    }

    function onAppInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // Whether accepted or dismissed, the prompt can't be reused.
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  if (installed || !deferredPrompt) return null;

  return (
    <button
      type="button"
      onClick={handleInstall}
      className={
        className ??
        "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-copper border border-copper/40 hover:bg-copper/10 transition-colors"
      }
      title="Install FoxCRM as an app"
      aria-label="Install FoxCRM as an app"
    >
      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4v12m0 0l-4-4m4 4l4-4M4 18v1a2 2 0 002 2h12a2 2 0 002-2v-1"
        />
      </svg>
      Install app
    </button>
  );
}
